import { useTagColors } from "@mapcraft-tasks/client-store/react";
import type { TagColor } from "@mapcraft-tasks/domain";
import type { ReactNode } from "react";

/**
 * Плашка тега — одна на строку аутлайна и редактор тегов: в редакторе она обязана
 * выглядеть ровно так, как потом в строке. Цвет приходит ключом палитры в `data-color`,
 * а оттенок под тему подбирает CSS (`.tag-chip[data-color]`).
 */
export function TagChip({
	tag,
	color,
	className = "",
	title,
	active,
	onPick,
	children
}: {
	tag: string;
	color: TagColor | undefined;
	className?: string;
	title?: string;
	/** Плашка выбрана (в редакторе — открыта её палитра). */
	active?: boolean;
	/**
	 * Щелчок по самой плашке. Плашка остаётся `span`, а не `button`: внутри неё живёт
	 * кнопка-крестик, а кнопка в кнопке — невалидная разметка, и браузер её разваливает.
	 * С клавиатуры палитра не нужна — тег добавляют набором, а цвет выбирают редко.
	 */
	onPick?: () => void;
	children?: ReactNode;
}) {
	return (
		<span
			className={`tag-chip ${className}`}
			data-color={color}
			data-active={active || undefined}
			title={title}
			onMouseDown={onPick ? (e) => e.preventDefault() : undefined}
			onClick={onPick}
		>
			#{tag}
			{children}
		</span>
	);
}

/**
 * Теги в строке аутлайна. Отдельным компонентом ради подписки: на карту цветов
 * подписываются только строки, у которых теги есть, а не весь аутлайн.
 *
 * Плашка здесь — кнопка: щелчок открывает редактор тегов этой ноды. Кнопкой, а не `span`
 * с обработчиком, потому что строка уже умеет пропускать кнопки — ни выделение по
 * `mousedown`, ни правка по тапу на телефоне на них не срабатывают.
 */
export function RowTags({ tags, onOpen }: { tags: readonly string[]; onOpen: () => void }) {
	const colors = useTagColors();
	return tags.map((tag) => (
		<button
			key={tag}
			type="button"
			className="tag-chip tag-chip-pick py-px"
			data-color={colors.get(tag)}
			title="теги"
			// Строке `mousedown` не отдаём: она сбросила бы мультивыделение до этой строки
			// раньше, чем `onOpen` решит, чьи теги править. Фокус кнопке тоже не нужен —
			// его тут же заберёт поле редактора.
			onMouseDown={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onClick={(e) => {
				e.stopPropagation();
				onOpen();
			}}
		>
			#{tag}
		</button>
	));
}
